'use client'

import { useEffect, useImperativeHandle, useRef, type Ref } from 'react'

import { rainFrame } from './rain-frame'

interface Drop {
  x: number
  y: number
  vy: number
  vx: number
  len: number
  alpha: number
}

export type RainOverlayHandle = { getPhase: () => number }

interface RainOverlayProps {
  seed?: number
  phase?: number
  ref?: Ref<RainOverlayHandle>
  dropCount?: number
  wind?: number
  colour?: string
  className?: string
  /** Paint one still frame of scattered rain, whether or not it is on screen. */
  freeze?: boolean
  /** Multiplies drop length and line width, for canvases far bigger than a viewport. */
  lineScale?: number
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min)
}

export function RainOverlay({
  dropCount = 200,
  wind = -0.08,
  colour = '174,194,224',
  className = '',
  freeze = false,
  lineScale = 1,
  seed,
  phase = 0,
  ref,
}: RainOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const phaseRef = useRef(phase)
  useImperativeHandle(ref, () => ({ getPhase: () => phaseRef.current }), [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Seeded absolute-time rain is used by the social artwork; leave the site's
    // original random, mobile-budgeted animation unchanged when no seed is supplied.
    if (seed !== undefined) {
      let raf = 0
      let visible = false
      let last = 0
      phaseRef.current = phase
      const drawFrame = () => {
        const w = canvas.offsetWidth,
          h = canvas.offsetHeight
        ctx.clearRect(0, 0, w, h)
        for (const drop of rainFrame({
          width: w,
          height: h,
          count: dropCount,
          wind,
          lineScale,
          seed,
          phase: phaseRef.current,
        })) {
          ctx.beginPath()
          ctx.moveTo(drop.x, drop.y)
          ctx.lineTo(drop.x - drop.vx * (drop.len / drop.vy), drop.y - drop.len)
          ctx.strokeStyle = `rgba(${colour},${drop.alpha})`
          ctx.lineWidth = (drop.len > 25 * lineScale ? 0.8 : 0.4) * lineScale
          ctx.stroke()
        }
      }
      const draw = (now: number) => {
        if (last) phaseRef.current += Math.min(100, now - last) / 1000
        last = now
        drawFrame()
        if (visible && !freeze) raf = requestAnimationFrame(draw)
      }
      const resize = () => {
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        drawFrame()
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting
          cancelAnimationFrame(raf)
          last = 0
          if (visible && !freeze) raf = requestAnimationFrame(draw)
        },
        { rootMargin: '200px' }
      )
      const resizeObserver = new ResizeObserver(resize)
      resize()
      observer.observe(canvas)
      resizeObserver.observe(canvas)
      return () => {
        cancelAnimationFrame(raf)
        observer.disconnect()
        resizeObserver.disconnect()
      }
    }

    let animId = 0
    let isVisible = false
    let lastTime = 0
    let w = 0
    let h = 0

    const isMobile = !freeze && window.innerWidth < 768
    const count = isMobile ? Math.round(dropCount * 0.25) : dropCount
    let frameSkip = 0

    const drops: Drop[] = []

    function resetDrop(drop: Drop, scatter: boolean) {
      const scale = Math.random()
      drop.x = rand(-40, w + 40)
      drop.y = scatter ? rand(-h * 0.5, h) : rand(-200, 0)
      drop.vy = lerp(0.25, 0.55, scale)
      drop.vx = wind
      drop.len = lerp(15, 35, scale) * lineScale
      drop.alpha = lerp(0.06, 0.18, scale)
    }

    function initDrops() {
      drops.length = 0
      for (let i = 0; i < count; i++) {
        const drop: Drop = { x: 0, y: 0, vy: 0, vx: 0, len: 0, alpha: 0 }
        resetDrop(drop, true)
        drops.push(drop)
      }
    }

    function resize() {
      if (!canvas) return
      w = canvas.offsetWidth
      h = canvas.offsetHeight
      canvas.width = w
      canvas.height = h
      initDrops()
      if (freeze) animId = requestAnimationFrame(draw)
    }

    function draw(now: number) {
      if (!canvas || !ctx) return

      // Mobile: skip every other frame for ~30fps. The drops are slow
      // enough that 30fps still reads as continuous rain.
      if (isMobile) {
        frameSkip++
        if (frameSkip % 2 !== 0) {
          if (isVisible) animId = requestAnimationFrame(draw)
          return
        }
      }

      if (!lastTime) lastTime = now
      let dt = now - lastTime
      lastTime = now

      if (dt > 100) dt = 16

      ctx.clearRect(0, 0, w, h)

      for (let i = 0; i < drops.length; i++) {
        const drop = drops[i]
        drop.x += drop.vx * dt
        drop.y += drop.vy * dt

        if (drop.y > h + drop.len) {
          resetDrop(drop, false)
        }

        const x1 = drop.x
        const y1 = drop.y
        const x2 = x1 - drop.vx * (drop.len / drop.vy)
        const y2 = y1 - drop.len

        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.strokeStyle = `rgba(${colour},${drop.alpha})`
        ctx.lineWidth = (drop.len > 25 * lineScale ? 0.8 : 0.4) * lineScale
        ctx.stroke()
      }

      if (isVisible && !freeze) animId = requestAnimationFrame(draw)
    }

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          isVisible = true
          lastTime = 0
          animId = requestAnimationFrame(draw)
        } else if (!entry.isIntersecting && isVisible) {
          isVisible = false
          cancelAnimationFrame(animId)
        }
      },
      { rootMargin: '200px' }
    )
    visibilityObserver.observe(canvas)

    resize()
    const resizeObserver = new ResizeObserver(resize)
    resizeObserver.observe(canvas)

    return () => {
      cancelAnimationFrame(animId)
      resizeObserver.disconnect()
      visibilityObserver.disconnect()
    }
  }, [dropCount, wind, colour, freeze, lineScale, seed, phase])

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 z-[2] h-full w-full ${className}`}
      aria-hidden="true"
    />
  )
}
